"use client";

import { useMemo, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Sheet, SheetBody, SheetClose, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type {
  ScorecardDefinitionCreate,
  ScorecardDefinitionOut,
  ScorecardDefinitionPatch,
} from "@/lib/api/model";

import type { ScorecardField, SelectionRule } from "../../lib/types";
import { validateFields } from "../../lib/validate-fields";
import { ArchivedBadge, FieldRow, SectionCard } from "../shared";
import { ScorecardFieldEditor } from "./field-editor";
import { SelectionRuleEditor } from "./rule-editor";

export interface DefinitionPayload {
  name: string;
  fields?: ScorecardField[];
  selection_rule?: SelectionRule | null;
  clear_selection_rule?: boolean;
}

/** Generated field items are `{ [key: string]: unknown }`; ScorecardField is the app shape. */
export function toCreateBody(p: DefinitionPayload): ScorecardDefinitionCreate {
  return {
    name: p.name,
    fields: (p.fields ?? []) as unknown as ScorecardDefinitionCreate["fields"],
    selection_rule: p.selection_rule ?? undefined,
  };
}

export function toPatchBody(p: DefinitionPayload): ScorecardDefinitionPatch {
  return {
    name: p.name,
    fields: p.fields as unknown as ScorecardDefinitionPatch["fields"],
    selection_rule: p.selection_rule ?? undefined,
    clear_selection_rule: p.clear_selection_rule ?? false,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "job" | "library";
  definition?: ScorecardDefinitionOut;
  stages?: { id: string; name: string }[];
  onSubmit: (payload: DefinitionPayload) => Promise<void>;
  onApplyLibraryUpdate?: () => Promise<void>;
  serverFieldErrors?: Record<string, string>;
  busy?: boolean;
  readOnly?: boolean;
}

export function ScorecardDefinitionSheet(props: Props) {
  const { open, onOpenChange, definition } = props;
  return (
    <Sheet open={open} onOpenChange={onOpenChange} size="xl" style={{ maxWidth: 700 }}>
      <DefinitionSheetForm
        key={definition?.id ?? "new"}
        {...props}
      />
    </Sheet>
  );
}

function DefinitionSheetForm({
  onOpenChange,
  mode,
  definition,
  stages,
  onSubmit,
  onApplyLibraryUpdate,
  serverFieldErrors,
  busy,
  readOnly,
}: Props) {
  const [name, setName] = useState(definition?.name ?? "");
  const [fields, setFields] = useState<ScorecardField[]>(
    () => (definition?.current_version.fields as ScorecardField[] | undefined) ?? [],
  );
  const [rule, setRule] = useState<SelectionRule | null>(
    () => (definition?.selection_rule as SelectionRule | null) ?? null,
  );
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(
    () => ({ ...(submitted ? validateFields(fields) : {}), ...(serverFieldErrors ?? {}) }),
    [fields, submitted, serverFieldErrors],
  );
  const initialFields = JSON.stringify(definition?.current_version.fields ?? null);
  const fieldsChanged = JSON.stringify(fields) !== initialFields;

  const submit = async () => {
    setSubmitted(true);
    if (Object.keys(validateFields(fields)).length || !name.trim()) return;
    await onSubmit({
      name: name.trim(),
      fields: !definition || fieldsChanged ? fields : undefined,
      selection_rule: rule ?? undefined,
      clear_selection_rule: definition ? rule === null && definition.selection_rule != null : undefined,
    });
  };

  const editorKey = definition?.id ?? "new";
  const nameError = submitted && !name.trim() ? "Name is required." : undefined;

  return (
    <>
      <SheetHeader>
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon name="IconChecklist" size={18} className="shrink-0 text-heading" aria-hidden="true" />
          <SheetTitle>
            {definition
              ? readOnly
                ? definition.name
                : `Edit ${definition.name}`
              : mode === "library"
                ? "New library type"
                : "New scorecard type"}
          </SheetTitle>
          {definition?.archived_at && <ArchivedBadge />}
        </div>
        <SheetClose aria-label="Close editor" />
      </SheetHeader>
      <SheetBody className="gap-5 bg-contrast/2">
        {!readOnly && definition?.library_update_available && onApplyLibraryUpdate && (
          <Alert.Container role="status" variant="warning" dir="vertical">
            <Alert.Icon>
              <Icon name="IconWarningSign" size={16} aria-hidden="true" />
            </Alert.Icon>
            <Alert.Title>Library version changed</Alert.Title>
            <Alert.Content>
              The library copy of this type has new fields. Apply the update to this job, or keep yours — nothing is
              applied for you.
              <div className="mt-2.5 flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onApplyLibraryUpdate()}
                >
                  Apply update
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                  Keep mine
                </Button>
              </div>
            </Alert.Content>
          </Alert.Container>
        )}

        <SectionCard iconName="IconChecklist" title="Details">
          <FieldRow
            label="Name"
            htmlFor="sc-name"
            helper="What interviewers see when they pick a scorecard."
            error={nameError}
          >
            <Input
              id="sc-name"
              variant="modal"
              placeholder="e.g. Technical interview"
              value={name}
              disabled={busy || readOnly}
              onChange={(e) => setName(e.target.value)}
            />
          </FieldRow>
        </SectionCard>

        <ScorecardFieldEditor
          key={`${editorKey}-fields`}
          value={fields}
          onChange={setFields}
          errors={errors}
          disabled={busy}
          readOnly={readOnly}
        />
        <SelectionRuleEditor
          key={`${editorKey}-rule`}
          value={rule}
          onChange={setRule}
          stages={mode === "job" ? stages : undefined}
          disabled={busy}
          readOnly={readOnly}
        />
      </SheetBody>
      <SheetFooter>
        {readOnly ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <SubmitButton
              type="button"
              variant="light"
              size="sm"
              disabled={busy}
              onClick={() => void submit()}
            >
              {definition ? "Save" : "Create"}
            </SubmitButton>
          </>
        )}
      </SheetFooter>
    </>
  );
}
